import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Bell, BellOff, Pencil, Trash2, Plus, Loader2, Search } from 'lucide-react';
import useThemeStore from '../store/themeStore';
import useTokenStore from '../store/store';
import { ThemeProps } from '../utils/theme';
import backgroundPattern from '../images/vesta/vesta-property-pattern-01-teal-bg.svg';
import ConfirmationModal from '../components/ConfirmationModal';
import { useSavedSearches } from '../hooks/useSavedSearches';
import { useSavedSearchMatches } from '../hooks/useSavedSearchMatches';
import { formatSearchCriteria } from '../utils/formatSearchCriteria';
import { visibleAccent } from '../utils/colourContrast';
import {
  createSavedSearch,
  updateSavedSearch,
  deleteSavedSearch,
  fetchSearchFacets,
  SearchFacets,
} from '../utils/savedSearches';
import { SavedSearch, SavedSearchCriteria, SavedSearchMatch } from '../types/savedSearch';
import {
  PRICE_BOUND_OPTIONS,
  BEDROOM_BOUND_OPTIONS,
  YIELD_BOUND_OPTIONS,
  BoundOption,
} from '../constants/searchFilters';

// Form state keeps every field as a string ('' = unset) so selects stay
// controlled; toApiCriteria converts to the numeric/omitted API shape.
interface SearchFormState {
  name: string;
  region: string;
  town: string;
  propertyType: string;
  strategy: string;
  minPrice: string;
  maxPrice: string;
  minBedRoom: string;
  maxBedRoom: string;
  minGrossYield: string;
  maxGrossYield: string;
  emailEnabled: boolean;
}

const EMPTY_FORM: SearchFormState = {
  name: '',
  region: '',
  town: '',
  propertyType: '',
  strategy: '',
  minPrice: '',
  maxPrice: '',
  minBedRoom: '',
  maxBedRoom: '',
  minGrossYield: '',
  maxGrossYield: '',
  emailEnabled: true,
};

export function toApiCriteria(form: SearchFormState): SavedSearchCriteria {
  const criteria: SavedSearchCriteria = {};
  if (form.region) criteria.region = form.region;
  if (form.town) criteria.town = form.town;
  if (form.propertyType) criteria.propertyType = form.propertyType;
  if (form.strategy) criteria.strategy = form.strategy;
  if (form.minPrice !== '') criteria.minPrice = Number(form.minPrice);
  if (form.maxPrice !== '') criteria.maxPrice = Number(form.maxPrice);
  if (form.minBedRoom !== '') criteria.minBedRoom = Number(form.minBedRoom);
  if (form.maxBedRoom !== '') criteria.maxBedRoom = Number(form.maxBedRoom);
  if (form.minGrossYield !== '') criteria.minGrossYield = Number(form.minGrossYield);
  if (form.maxGrossYield !== '') criteria.maxGrossYield = Number(form.maxGrossYield);
  return criteria;
}

export function fromSavedSearch(search: SavedSearch): SearchFormState {
  const { criteria } = search;
  const asString = (value?: number) => (typeof value === 'number' ? String(value) : '');
  return {
    name: search.name,
    region: criteria.region ?? '',
    town: criteria.town ?? '',
    propertyType: criteria.propertyType ?? '',
    strategy: criteria.strategy ?? '',
    minPrice: asString(criteria.minPrice),
    maxPrice: asString(criteria.maxPrice),
    minBedRoom: asString(criteria.minBedRoom),
    maxBedRoom: asString(criteria.maxBedRoom),
    minGrossYield: asString(criteria.minGrossYield),
    maxGrossYield: asString(criteria.maxGrossYield),
    emailEnabled: search.emailEnabled !== false,
  };
}

const matchLink = (match: SavedSearchMatch): string =>
  match.propertyKind === 'newBuild'
    ? `/new-build/${match.propertyId}`
    : `/listing/${match.propertyId}`;

const SavedSearchesPage: React.FC = () => {
  const getThemes = useThemeStore((state) => state.theme as unknown as ThemeProps);
  const getToken = useTokenStore((state) => state.token);

  const { searches, loading: searchesLoading, refetch } = useSavedSearches();
  const { matches, loading: matchesLoading, loadMore, hasMore } = useSavedSearchMatches();

  const [facets, setFacets] = useState<SearchFacets | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<SearchFormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SavedSearch | null>(null);

  useEffect(() => {
    let active = true;
    fetchSearchFacets().then((result) => {
      if (active) setFacets(result);
    });
    return () => {
      active = false;
    };
  }, []);

  // White-on-white guard: skip a white colour 1 in favour of colour 2.
  const themeColour = visibleAccent(getThemes?.color1 || '', getThemes?.color2, '#1A2347');

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setFormError(null);
    setFormOpen(true);
  };

  const openEdit = (search: SavedSearch) => {
    setForm(fromSavedSearch(search));
    setEditingId(search._id);
    setFormError(null);
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!getToken || saving) return;
    const criteria = toApiCriteria(form);
    if (!form.name.trim()) {
      setFormError('Please name your search.');
      return;
    }
    if (Object.keys(criteria).length === 0) {
      setFormError('Choose at least one filter — a search with no filters would match every property.');
      return;
    }
    setSaving(true);
    setFormError(null);
    const input = { name: form.name.trim(), criteria, emailEnabled: form.emailEnabled };
    const result = editingId
      ? await updateSavedSearch(getToken, editingId, input)
      : await createSavedSearch(getToken, input);
    setSaving(false);
    if (result.ok) {
      setFormOpen(false);
      refetch();
    } else {
      setFormError(result.message || 'Something went wrong — please try again.');
    }
  };

  const handleToggleEmail = async (search: SavedSearch) => {
    if (!getToken) return;
    const result = await updateSavedSearch(getToken, search._id, {
      name: search.name,
      criteria: search.criteria,
      emailEnabled: !(search.emailEnabled !== false),
    });
    if (result.ok) refetch();
  };

  const handleDelete = async () => {
    if (!getToken || !deleteTarget) return;
    const result = await deleteSavedSearch(getToken, deleteTarget._id);
    setDeleteTarget(null);
    if (result.ok) refetch();
  };

  const boundSelect = (
    label: string,
    minKey: keyof SearchFormState,
    maxKey: keyof SearchFormState,
    options: BoundOption[]
  ) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <select
          className="w-full border border-gray-300 rounded-md p-2 text-sm bg-white"
          value={form[minKey] as string}
          onChange={(e) => setForm((f) => ({ ...f, [minKey]: e.target.value }))}
          aria-label={`Minimum ${label.toLowerCase()}`}
        >
          <option value="">No min</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <span className="text-gray-400">–</span>
        <select
          className="w-full border border-gray-300 rounded-md p-2 text-sm bg-white"
          value={form[maxKey] as string}
          onChange={(e) => setForm((f) => ({ ...f, [maxKey]: e.target.value }))}
          aria-label={`Maximum ${label.toLowerCase()}`}
        >
          <option value="">No max</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const facetSelect = (
    label: string,
    key: keyof SearchFormState,
    options: string[] | undefined
  ) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <select
        className="w-full border border-gray-300 rounded-md p-2 text-sm bg-white"
        value={form[key] as string}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        aria-label={label}
      >
        <option value="">Any</option>
        {(options || []).map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col pt-28">
      <Helmet>
        <title>Saved Searches</title>
      </Helmet>
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-5"
        style={{ backgroundImage: `url(${backgroundPattern})` }}
      />

      <div className="relative z-10 flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto space-y-10">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-2" style={{ color: getThemes?.txtColor }}>
              Saved Searches
            </h1>
            <p className="text-lg text-black">
              Save your property filters and we&rsquo;ll alert you when new matches are listed
            </p>
          </div>

          {/* New matches */}
          <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">New matches</h2>
            {matchesLoading && matches.length === 0 ? (
              <div className="flex items-center text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading matches…
              </div>
            ) : matches.length === 0 ? (
              <p className="text-gray-500">
                No matches yet — new properties that fit your saved searches will appear here.
              </p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {matches.map((match) => (
                  <li key={match._id} className="py-3">
                    <Link
                      to={matchLink(match)}
                      className="flex items-center gap-4 group"
                    >
                      {match.propertySnapshot?.imageUrl ? (
                        <img
                          src={match.propertySnapshot.imageUrl}
                          alt=""
                          className="w-16 h-12 object-cover rounded-md flex-shrink-0"
                        />
                      ) : (
                        <div className="w-16 h-12 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <Search className="w-5 h-5 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate group-hover:underline">
                          {match.propertySnapshot?.propertyAddress || 'View property'}
                        </p>
                        <p className="text-sm text-gray-500 truncate">
                          {[
                            typeof match.propertySnapshot?.price === 'number'
                              ? `£${match.propertySnapshot.price.toLocaleString('en-GB')}`
                              : null,
                            typeof match.propertySnapshot?.bedrooms === 'number'
                              ? `${match.propertySnapshot.bedrooms} bed`
                              : null,
                            match.propertySnapshot?.town,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                        <p className="text-xs text-gray-400 truncate">
                          Matched {match.searchNames.map((name) => `“${name}”`).join(', ')}
                        </p>
                      </div>
                      {!match.seenAt && (
                        <span
                          className="text-xs font-semibold px-2 py-1 rounded-full text-white flex-shrink-0"
                          style={{ backgroundColor: themeColour }}
                        >
                          New
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {hasMore && (
              <button
                onClick={loadMore}
                disabled={matchesLoading}
                className="mt-4 px-4 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                {matchesLoading ? 'Loading…' : 'Load more'}
              </button>
            )}
          </section>

          {/* Saved searches */}
          <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Your saved searches</h2>
              {!formOpen && (
                <button
                  onClick={openCreate}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white"
                  style={{ backgroundColor: themeColour }}
                >
                  <Plus className="w-4 h-4 mr-1" /> Create search
                </button>
              )}
            </div>

            {formOpen && (
              <div className="border border-gray-200 rounded-lg p-4 mb-6 bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  {editingId ? 'Edit search' : 'New search'}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Search name
                    </label>
                    <input
                      type="text"
                      maxLength={60}
                      className="w-full border border-gray-300 rounded-md p-2 text-sm"
                      placeholder="e.g. Leeds 3-beds"
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    />
                  </div>
                  {facetSelect('Region', 'region', facets?.regions)}
                  {facetSelect('Town', 'town', facets?.towns)}
                  {facetSelect('Property type', 'propertyType', facets?.propertyType)}
                  {facetSelect('Strategy', 'strategy', facets?.strategy)}
                  {boundSelect('Price', 'minPrice', 'maxPrice', PRICE_BOUND_OPTIONS)}
                  {boundSelect('Bedrooms', 'minBedRoom', 'maxBedRoom', BEDROOM_BOUND_OPTIONS)}
                  {boundSelect('Gross yield', 'minGrossYield', 'maxGrossYield', YIELD_BOUND_OPTIONS)}
                  <div className="flex items-end pb-1">
                    <label className="flex items-center text-sm text-gray-700 gap-2">
                      <input
                        type="checkbox"
                        checked={form.emailEnabled}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, emailEnabled: e.target.checked }))
                        }
                      />
                      Email me when new properties match
                    </label>
                  </div>
                </div>
                {formError && <p className="text-sm text-red-600 mt-3">{formError}</p>}
                <div className="flex justify-end gap-3 mt-4">
                  <button
                    onClick={() => setFormOpen(false)}
                    className="px-4 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 text-sm font-medium rounded-md text-white disabled:opacity-50"
                    style={{ backgroundColor: themeColour }}
                  >
                    {saving ? 'Saving…' : editingId ? 'Save changes' : 'Save search'}
                  </button>
                </div>
              </div>
            )}

            {searchesLoading ? (
              <div className="flex items-center text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading searches…
              </div>
            ) : searches.length === 0 && !formOpen ? (
              <p className="text-gray-500">
                No saved searches yet — create one and we&rsquo;ll email you when a matching
                property is listed.
              </p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {searches.map((search) => (
                  <li key={search._id} className="py-3 flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{search.name}</p>
                      <p className="text-sm text-gray-500 truncate">
                        {formatSearchCriteria(search.criteria)}
                      </p>
                    </div>
                    <button
                      onClick={() => handleToggleEmail(search)}
                      className="p-2 rounded-md hover:bg-gray-100"
                      title={
                        search.emailEnabled !== false
                          ? 'Email alerts on — click to turn off for this search'
                          : 'Email alerts off — click to turn on for this search'
                      }
                    >
                      {search.emailEnabled !== false ? (
                        <Bell className="w-4 h-4" style={{ color: themeColour }} />
                      ) : (
                        <BellOff className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                    <button
                      onClick={() => openEdit(search)}
                      className="p-2 rounded-md hover:bg-gray-100"
                      title="Edit search"
                    >
                      <Pencil className="w-4 h-4 text-gray-600" />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(search)}
                      className="p-2 rounded-md hover:bg-gray-100"
                      title="Delete search"
                    >
                      <Trash2 className="w-4 h-4 text-gray-600" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>

      <ConfirmationModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete saved search"
        message={`Delete “${deleteTarget?.name ?? ''}”? You will no longer receive alerts for it.`}
        type="warning"
      />
    </div>
  );
};

export default SavedSearchesPage;
